// apps/web/src/lib/vendas/preco-item.ts — Fase 12, Plano 07 (PV-01, D-06, D-15).
// Regras puras do preço do item no formulário do PV: o preço vem da tabela (servidor precifica) e só é
// enviado quando o usuário o sobrescreve; divergência e "sem preço" apenas sinalizam, nunca bloqueiam.
import type { CodigoPendenciaPv, PendenciaItemPv } from '@pieltec/contracts';
import { formatarMoeda } from './formatacao';

export type EstadoPrecoItem =
  /** Sem material: não há como consultar a tabela. */
  | { tipo: 'indefinido' }
  /** Vale o preço da tabela (não sobrescrito, ou informado igual ao da tabela). */
  | { tipo: 'tabela'; tabela: number }
  /** Informado difere da tabela vigente (comparação em centavos, como o servidor). */
  | { tipo: 'divergente'; tabela: number; informado: number }
  /** Informado, sem preço na tabela para comparar. */
  | { tipo: 'manual' }
  /** Material informado, sem preço na tabela e nada digitado (D-06: segue para produção). */
  | { tipo: 'sem-preco' };

export interface EntradaEstadoPreco {
  readonly temMaterial: boolean;
  /** Preço da tabela vigente hoje na unidade do item; null sem versão aplicável. */
  readonly tabela: number | null;
  /** Preço digitado pelo usuário (NaN/null quando vazio). */
  readonly informado: number | null;
  /** O usuário mexeu no campo de preço. */
  readonly sobrescrito: boolean;
}

function centavos(valor: number): number {
  return Math.round(valor * 100);
}

export function estadoPrecoItem({ temMaterial, tabela, informado, sobrescrito }: EntradaEstadoPreco): EstadoPrecoItem {
  const valorInformado = sobrescrito && informado !== null && Number.isFinite(informado) && informado >= 0 ? informado : null;
  if (valorInformado !== null) {
    if (tabela === null) return { tipo: 'manual' };
    return centavos(valorInformado) === centavos(tabela) ? { tipo: 'tabela', tabela } : { tipo: 'divergente', tabela, informado: valorInformado };
  }
  if (tabela !== null) return { tipo: 'tabela', tabela };
  return temMaterial ? { tipo: 'sem-preco' } : { tipo: 'indefinido' };
}

/** Copy do UI-SPEC: alerta amber quando o preço informado diverge da tabela. */
export function copiaPrecoDiverge(tabela: number, informado: number): string {
  return `Preço diverge da tabela (${formatarMoeda(tabela)} → ${formatarMoeda(informado)}).`;
}

export const COPIA_SEM_PRECO_TABELA = 'Sem preço na tabela. O item segue para produção, mas o fechamento ficará pendente até haver preço.';

/** Preço a enviar na criação: só quando o usuário sobrescreveu (senão o servidor precifica, D-15). */
export function precoParaEnvio(sobrescrito: boolean, informado: number | null): number | null {
  return sobrescrito && informado !== null && Number.isFinite(informado) && informado >= 0 ? informado : null;
}

export interface RascunhoParaPendencias {
  readonly materialId?: string | null;
  readonly precoUnitario?: number | null;
  /** Preço da tabela sugerido no momento em que o item foi salvo. */
  readonly precoTabela?: number | null;
}

/** Pendências do item ainda em rascunho (a grade de resumo usa os mesmos badges do detalhe). */
export function pendenciasDoRascunho(item: RascunhoParaPendencias): PendenciaItemPv[] {
  const pendencias: PendenciaItemPv[] = [];
  const adicionar = (codigo: CodigoPendenciaPv, bloqueia: PendenciaItemPv['bloqueia']): void => {
    pendencias.push({ codigo, bloqueia, detalhe: null });
  };
  if (!item.materialId) adicionar('MATERIAL_NAO_VINCULADO', 'OP');
  const tabela = item.precoTabela ?? null;
  const informado = item.precoUnitario ?? null;
  if (informado === null && tabela === null) adicionar('SEM_PRECO', 'RASCUNHO_FISCAL');
  if (informado !== null && tabela !== null && centavos(informado) !== centavos(tabela)) adicionar('PRECO_DIVERGE', null);
  return pendencias;
}
